import { useCallback } from "react";
import logger from "../utils/logger";
import { useBulkOperationsStore } from "../stores";

/**
 * Hook for routing WebSocket messages to appropriate event handlers
 * Extracted from AppContent.js (Phase 1 Day 45)
 */
export function useWebSocketMessageRouter({
  credentialEventHandlers,
  deviceEventHandlers,
  actionEventHandlers,
  securityEventHandlers,
  systemEventHandlers,
  orgSettings,
  showNotification,
}) {
  const {
    checkResetDeviceStateUpdateTracking,
    incrementDeviceStateUpdateCounter,
    updateDeviceTracking,
    checkResetBulkLockdownTracking,
    setLastLockdownEventTime,
  } = useBulkOperationsStore();

  // WebSocket message handler - processes all incoming WebSocket messages
  // This is registered with the WebSocket store and handles all business logic for message processing
  const handleWebSocketMessage = useCallback(
    (message) => {
      try {
        // Log event type and action details for debugging
        logger.log(
          `[WebSocket] Event type: ${message.eventType}, DeviceID: ${
            message.deviceId
          }, ActionID: ${message.actionId || "N/A"}`
        );

        // 🔍 ENHANCED LOGGING: Track all deviceStateUpdate events with full details
        if (message.eventType === "deviceStateUpdate") {
          checkResetDeviceStateUpdateTracking();
          incrementDeviceStateUpdateCounter();
          const counter =
            useBulkOperationsStore.getState().deviceStateUpdateCounter;
          logger.log(
            `🔍 [WebSocket] deviceStateUpdate #${counter} DETECTED - Device: ${
              message.deviceId
            }, lockdownState: ${message.lockdownState}, lockState: ${
              message.lockState
            }, Timestamp: ${new Date().toISOString()}`
          );
        }

        switch (message.eventType) {
          case "stateRefreshed":
            systemEventHandlers.handleStateRefreshed(message);
            break;

          case "dataRefreshed":
            systemEventHandlers.handleDataRefreshed(message);
            break;

          case "credentialStatusChanged":
            credentialEventHandlers.handleCredentialStatusChanged(message);
            break;

          case "credentialProvisioned":
            credentialEventHandlers.handleCredentialProvisioned(message);
            break;

          case "credentialProvisionRejected":
            credentialEventHandlers.handleCredentialProvisionRejected(message);
            break;

          case "credentialRemovedFromDevice":
            credentialEventHandlers.handleCredentialRemovedFromDevice(message);
            break;

          case "credentialDownloaded":
            credentialEventHandlers.handleCredentialDownloaded(message);
            // Show notification if enabled in settings
            if (orgSettings.receiveCredentialNotifications !== false) {
              showNotification({
                message: `Credential downloaded successfully. Add it to devices when ready.`,
                type: "success",
              });
            }
            break;

          case "lastActionCreated":
            actionEventHandlers.handleLastActionCreated(message);
            break;

          case "lastActionUpdated":
            actionEventHandlers.handleLastActionUpdated(message);
            break;

          case "deviceStateUpdate":
            deviceEventHandlers.handleDeviceStateUpdate(message);

            // Bulk lockdown tracking using bulkOperationsStore (Phase 3D PR #1)
            const deviceId = message.deviceId;
            const stateUpdates = { ...message };
            delete stateUpdates.deviceId;

            // Track bulk lockdown events for logging summary
            if ("lockdownState" in stateUpdates) {
              const lockdownState = stateUpdates.lockdownState;
              if (
                lockdownState === "Lockdown On" ||
                lockdownState === "Lockdown Off"
              ) {
                checkResetBulkLockdownTracking();
                setLastLockdownEventTime(Date.now());
                updateDeviceTracking(deviceId, { lockdownState });
              }
            }

            // Track FROZEN lock states
            if ("lockState" in stateUpdates) {
              const lockStateValue = String(
                stateUpdates.lockState
              ).toUpperCase();
              if (lockStateValue.includes("FROZEN")) {
                checkResetBulkLockdownTracking();
                setLastLockdownEventTime(Date.now());
                updateDeviceTracking(deviceId, {
                  lockState: stateUpdates.lockState,
                });
              }
            }
            break;

          case "forcedEntryDetected":
            securityEventHandlers.handleForcedEntryDetected(message);
            break;

          case "proppedDoorDetected":
            securityEventHandlers.handleProppedDoorDetected(message);
            break;

          case "doorNotification":
            systemEventHandlers.handleDoorNotification(message);
            break;

          case "lockNotification":
            systemEventHandlers.handleLockNotification(message);
            break;

          case "patronUpdated":
            credentialEventHandlers.handlePatronUpdated(message);
            break;

          case "deviceClaimed":
            deviceEventHandlers.handleDeviceClaimed(message);
            break;

          case "deviceUnclaimed":
            deviceEventHandlers.handleDeviceUnclaimed(message);
            break;

          case "DEVICE_NOTIFICATION_REPORTED":
            deviceEventHandlers.handleDeviceNotificationReported(message);
            break;

          case "subscribeConfirmation":
            systemEventHandlers.handleSubscribeConfirmation(message);
            break;

          default:
            logger.log(
              `WebSocket: Received unhandled eventType: ${message.eventType}`
            );
            break;
        }
      } catch (error) {
        logger.error("[WebSocket] Message handler error:", error);
      }
    },
    [
      credentialEventHandlers,
      deviceEventHandlers,
      actionEventHandlers,
      securityEventHandlers,
      systemEventHandlers,
      orgSettings,
      showNotification,
      checkResetDeviceStateUpdateTracking,
      incrementDeviceStateUpdateCounter,
      updateDeviceTracking,
      checkResetBulkLockdownTracking,
      setLastLockdownEventTime,
    ]
  );

  return { handleWebSocketMessage };
}
